import type { AssignmentListItem } from '@repo/types/assignments';
import type { Dispatch, SetStateAction } from 'react';

import { useDeleteAssignment } from '@repo/api/assignment/useDeleteAssignment';
import { assignmentErrorMessages } from '@repo/lib/messages/assignmentErrorMessages';
import { ClipboardListIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface DeleteAssignmentProps {
  assignments?: AssignmentListItem[] | null;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export default function DeleteAssignment(deleteAssignmentProps: DeleteAssignmentProps) {
  const { assignments, open, onOpenChange } = deleteAssignmentProps;
  const { mutateAsync, isPending } = useDeleteAssignment();

  const isSingle = assignments?.length === 1;

  const onSubmit = async () => {
    if (!assignments?.length) return;
    try {
      for (const assignment of assignments) {
        await mutateAsync({ id: assignment.id });
      }
      onOpenChange(false);
    } catch (error) {
      toasterMessageHandler(assignmentErrorMessages, error instanceof Error ? error : null);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <ClipboardListIcon />
            </FeaturedIcon>
            <div className="flex min-w-0 grow flex-col gap-0.5">
              <AlertDialogTitle>Usunąć zadanie/a?</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground text-xs">Usuń wybrane zadanie/a.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>

          <div className="flex flex-col gap-3 overflow-auto p-4">
            {isSingle ? (
              <p className="text-text-secondary">
                Czy na pewno chcesz usunąć zadanie <b>{assignments?.[0]?.name}</b>?
              </p>
            ) : (
              <>
                <p className="text-text-secondary">Czy na pewno chcesz usunąć wybrane zadania?</p>
                <div className="flex flex-wrap gap-2">
                  {assignments?.map(({ name, id }) => (
                    <Badge key={id} size="sm" variant="muted">
                      {name}
                    </Badge>
                  ))}
                </div>
              </>
            )}
          </div>
        </AlertDialogBody>

        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant="tertiary" disabled={isPending}>
            Zamknij
          </AlertDialogCancel>

          <Button onClick={onSubmit} disabled={isPending}>
            <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
              Potwierdź
            </LoadingSwap>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
